import { useCallback, useEffect, useRef, useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { archiveConversation, fetchConversations } from "../smsApi";
import { formatPhone, lineLabel, OUR_NUMBERS } from "../phone";
import type { SmsConversation } from "../types";
import type { ContactMap } from "../useContacts";

const LIST_TYPE_COLORS: Record<string, string> = {
  Employee: "bg-blue-900/40 text-blue-300 border-blue-700/60",
  Customer: "bg-green-900/40 text-green-300 border-green-700/60",
  Member: "bg-purple-900/40 text-purple-300 border-purple-700/60",
  Other: "bg-tribal-border text-tribal-muted border-tribal-border",
};

const READ_MAP_KEY = "hq_sms_read_map";
// Belt-and-braces poll — Realtime is the primary delivery channel (see the
// `postgres_changes` subscription below). If Realtime stays healthy this
// poll fires rarely enough to cost ~1 req/min per open session; if Realtime
// drops, worst-case latency becomes 60s, which is fine for SMS.
const POLL_INTERVAL_MS = 60_000;

type Props = {
  phoneLine: "line1" | "line2" | null;
  selectedContact: string | null;
  onSelect: (conv: SmsConversation) => void;
  contactMap: ContactMap;
  refreshKey: number;
};

function timeAgo(dateStr: string): string {
  try {
    return formatDistanceToNowStrict(new Date(dateStr), { addSuffix: true });
  } catch {
    return "";
  }
}

export function ConversationList({
  phoneLine,
  selectedContact,
  onSelect,
  contactMap,
  refreshKey,
}: Props) {
  const [conversations, setConversations] = useState<SmsConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState<string | null>(null);
  // Without this a failed load rendered "No conversations yet", which reads as
  // a quiet day rather than an outage.
  const [loadError, setLoadError] = useState<string | null>(null);

  // `readMap` is persisted locally so unread badges survive refreshes.
  const readMapRef = useRef<Record<string, string>>(
    JSON.parse(localStorage.getItem(READ_MAP_KEY) || "{}")
  );

  const reload = useCallback(async () => {
    try {
      const rows = await fetchConversations({
        phoneLine,
        lastSeen: readMapRef.current,
      });
      setConversations(rows);
      setLoadError(null);
    } catch (err) {
      console.error("Failed to fetch conversations", err);
      setLoadError((err as Error).message || "Could not load conversations");
    } finally {
      setLoading(false);
    }
  }, [phoneLine]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload, refreshKey]);

  // Clear unread badge + persist last-seen timestamp when a conversation is opened.
  useEffect(() => {
    if (!selectedContact) return;
    setConversations((prev) => {
      const match = prev.find((c) => c.contact_number === selectedContact);
      if (match) {
        readMapRef.current[selectedContact] = match.last_message_at;
        localStorage.setItem(READ_MAP_KEY, JSON.stringify(readMapRef.current));
      }
      return prev.map((c) =>
        c.contact_number === selectedContact ? { ...c, unread_count: 0 } : c
      );
    });
  }, [selectedContact]);

  useEffect(() => {
    const interval = setInterval(reload, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [reload]);

  // Realtime: merge new inbound/outbound rows into the top of the list.
  // Track B cutover (April 22, 2026): flipped schema `public` → `sms`. The
  // table is still `sms_messages` (schema-only move; no rename).
  useEffect(() => {
    const channel = supabase
      .channel("hq-sms-conversations")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "sms", table: "sms_messages" },
        (payload) => {
          const msg = payload.new as {
            direction: "inbound" | "outbound";
            from_number: string;
            to_number: string;
            body: string;
            created_at: string;
            phone_line: string;
          };
          const contactNumber =
            msg.direction === "inbound" ? msg.from_number : msg.to_number;

          if (OUR_NUMBERS.includes(contactNumber)) return;
          if (phoneLine && msg.phone_line !== phoneLine) return;

          setConversations((prev) => {
            const idx = prev.findIndex((c) => c.contact_number === contactNumber);
            const updated: SmsConversation = {
              contact_number: contactNumber,
              last_message: msg.body,
              last_message_at: msg.created_at,
              unread_count:
                idx >= 0
                  ? prev[idx].unread_count + (msg.direction === "inbound" ? 1 : 0)
                  : msg.direction === "inbound"
                  ? 1
                  : 0,
              phone_line: msg.phone_line,
            };
            const next = idx >= 0 ? prev.filter((_, i) => i !== idx) : [...prev];
            return [updated, ...(idx >= 0 ? next : prev)];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [phoneLine]);

  async function handleArchive(e: React.MouseEvent, contactNumber: string) {
    e.stopPropagation();
    setArchiving(contactNumber);
    try {
      await archiveConversation(contactNumber);
      setConversations((prev) =>
        prev.filter((c) => c.contact_number !== contactNumber)
      );
    } catch (err) {
      console.error("Failed to archive conversation", err);
    } finally {
      setArchiving(null);
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        <div className="animate-pulse">Loading conversations…</div>
      </div>
    );
  }

  if (loadError && conversations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm">
        <div>
          <p className="font-medium text-red-300">Couldn't load conversations</p>
          <p className="mt-1 text-xs text-tribal-muted">{loadError}</p>
          <button
            onClick={() => { setLoading(true); reload(); }}
            className="mt-3 rounded border border-tribal-border px-3 py-1 text-xs text-white hover:bg-tribal-surface"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-tribal-muted">
        <div>
          <p className="font-medium">No conversations yet</p>
          <p className="mt-1">Messages will appear here when they come in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-tribal-border">
      {conversations.map((conv) => {
        const isActive = selectedContact === conv.contact_number;
        const hasUnread = conv.unread_count > 0;
        const isArchiving = archiving === conv.contact_number;
        const contact = contactMap[conv.contact_number];

        return (
          <div
            key={conv.contact_number}
            className={`group relative transition-colors hover:bg-white/5 ${
              isActive ? "bg-white/5" : ""
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(conv)}
              className="w-full px-4 py-3 pr-10 text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`truncate text-sm ${
                        hasUnread ? "font-bold text-white" : "text-gray-200"
                      }`}
                    >
                      {contact ? contact.name : formatPhone(conv.contact_number)}
                    </span>
                    {contact && (
                      <span
                        className={`flex-shrink-0 rounded border px-1.5 py-0.5 text-[10px] ${
                          LIST_TYPE_COLORS[contact.list_type] ??
                          LIST_TYPE_COLORS.Other
                        }`}
                      >
                        {contact.list_type}
                      </span>
                    )}
                    {conv.phone_line && (
                      <span className="flex-shrink-0 rounded bg-tribal-border px-1.5 py-0.5 text-[10px] font-medium text-tribal-muted">
                        {lineLabel(conv.phone_line)}
                      </span>
                    )}
                  </div>
                  {contact && (
                    <p className="truncate text-[10px] text-tribal-muted">
                      {formatPhone(conv.contact_number)}
                    </p>
                  )}
                  <p
                    className={`mt-0.5 truncate text-xs ${
                      hasUnread ? "font-medium text-gray-200" : "text-tribal-muted"
                    }`}
                  >
                    {conv.last_message && conv.last_message.length > 50
                      ? `${conv.last_message.slice(0, 50)}…`
                      : conv.last_message}
                  </p>
                </div>

                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  <span className="whitespace-nowrap text-[10px] text-tribal-muted">
                    {timeAgo(conv.last_message_at)}
                  </span>
                  {hasUnread && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-tribal-red text-[10px] font-bold text-white">
                      {conv.unread_count > 9 ? "9+" : conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={(e) => handleArchive(e, conv.contact_number)}
              disabled={isArchiving}
              title="Archive conversation"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-tribal-muted opacity-0 transition-all hover:bg-white/10 hover:text-red-400 group-hover:opacity-100"
            >
              {isArchiving ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-tribal-muted border-t-transparent" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
